import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useRoleSwitcher } from '../features/auth/hooks/useRoleSwitcher';
import { useMenuItemsContext } from '../contexts/MenuItemsContext';
import { useSchool } from '../contexts/SchoolContext';

/**
 * Context for pages to register their loading states with the orchestrator
 */
const LoadingOrchestratorContext = createContext(null);

/**
 * Hook for pages to register their loading state with enhanced debugging
 * @returns {Object} Loading orchestrator utilities
 * @returns {Function} returns.registerLoading - Call with (key, isLoading, priority) to register/update loading state
 * @returns {Function} returns.getDebugInfo - Get comprehensive debug information
 * @returns {Function} returns.getLoadingHistory - Get loading performance history
 * @returns {Function} returns.getActiveLoadings - Get currently active loading states
 */
export const useLoadingOrchestrator = () => {
  const context = useContext(LoadingOrchestratorContext);
  if (!context) {
    // Gracefully handle when not wrapped in orchestrator - just do nothing
    return { 
      registerLoading: () => {},
      getDebugInfo: () => ({}),
      getLoadingHistory: () => [],
      getActiveLoadings: () => ({})
    };
  }
  return context;
};

/**
 * LoadingOrchestrator - Single source of truth for ALL loading states
 *
 * Consolidates app-level AND page-specific loading checks into one component.
 * Shows a single skeleton until ALL loading states are complete.
 *
 * App-level loading (automatic):
 * - Auth initialization
 * - Role switching
 * - Menu loading
 * - Parent data initialization (when in parent role)
 *
 * Page-level loading (opt-in via useLoadingOrchestrator hook):
 * - Pages can register their data loading states
 * - Orchestrator waits for all registered states to complete
 *
 * IMPLEMENTATION GUIDE: See src/components/loading/USAGE.md for complete documentation
 * and the two-layer loading pattern (LoadingOrchestrator + StandardPage)
 *
 * @param {ReactNode} children - Content to render when all loading is complete
 * @param {ReactNode} skeleton - Loading skeleton to show while any state is loading
 */
const LoadingOrchestrator = ({ children, skeleton }) => {
  // Gather app-level loading states
  const { initializing } = useAuth();
  const { loading: roleLoading } = useRoleSwitcher();
  const { loading: menuLoading } = useMenuItemsContext();
  const { loading: schoolLoading } = useSchool();

  // Track page-specific loading states with performance tracking
  const [pageLoadingStates, setPageLoadingStates] = useState({});
  const pageLoadingRef = useRef({});
  const loadingStartTimeRef = useRef({});
  const loadingHistoryRef = useRef([]);

  // Performance tracking for loading states
  const trackLoadingPerformance = useCallback((key, isLoading) => {
    const now = Date.now();
    
    if (isLoading && !loadingStartTimeRef.current[key]) {
      // Loading started
      loadingStartTimeRef.current[key] = now;
      console.log(`[LoadingOrchestrator] 🟢 Started loading: ${key}`);
    } else if (!isLoading && loadingStartTimeRef.current[key]) {
      // Loading completed
      const duration = now - loadingStartTimeRef.current[key];
      const history = {
        key,
        duration,
        timestamp: now,
        completedAt: new Date().toISOString()
      };
      
      // Keep last 20 loading events
      loadingHistoryRef.current = [...loadingHistoryRef.current.slice(-19), history];
      
      console.log(`[LoadingOrchestrator] ✅ Completed loading: ${key} (${duration}ms)`);
      
      if (duration > 3000) {
        console.warn(`[LoadingOrchestrator] ⚠️ Slow loading detected: ${key} took ${duration}ms`);
      }
      
      delete loadingStartTimeRef.current[key];
    }
  }, []);

  // Register/update a page loading state with performance tracking
  const registerLoading = useCallback((key, isLoading, priority = 'normal') => {
    pageLoadingRef.current[key] = isLoading;
    setPageLoadingStates({ ...pageLoadingRef.current });
    trackLoadingPerformance(key, isLoading);
  }, [trackLoadingPerformance]);

  // App-level loading gates whether children mount. Page-level loading is
  // tracked for telemetry/debug only — gating mount on it caused a remount
  // loop, because a page registering loading=true on mount would unmount
  // itself, fire cleanup (loading=false), remount, repeat.
  const hasPageLoading = Object.values(pageLoadingStates).some(loading => loading === true);
  const isAppLoading = initializing || roleLoading || menuLoading || schoolLoading;
  const isLoading = isAppLoading || hasPageLoading;

  // Only show skeleton if loading takes longer than threshold (prevents flash)
  const [showSkeleton, setShowSkeleton] = useState(false);

  // Phase 1: Universal Timeout Protection
  const LOADING_TIMEOUT = 10000; // 10 seconds for individual loading states
  const DEADLOCK_TIMEOUT = 15000; // 15 seconds for complete app deadlock

  // Phase 1: Universal Timeout Protection
  useEffect(() => {
    const timeoutIds = {};
    
    // Set timeout for each active page loading state
    Object.entries(pageLoadingStates).forEach(([key, isLoading]) => {
      if (isLoading && !timeoutIds[key]) {
        timeoutIds[key] = setTimeout(() => {
          console.warn(`[LoadingOrchestrator] ⚠️ TIMEOUT: Forcing completion of stuck loading state: ${key}`);
          registerLoading(key, false); // Force completion
        }, LOADING_TIMEOUT);
      } else if (!isLoading && timeoutIds[key]) {
        clearTimeout(timeoutIds[key]);
        delete timeoutIds[key];
      }
    });
    
    return () => {
      Object.values(timeoutIds).forEach(clearTimeout);
    };
  }, [pageLoadingStates, registerLoading]);

  // Phase 1: Deadlock Detection & Forced Completion
  useEffect(() => {
    const deadlockTimer = setTimeout(() => {
      if (isLoading) {
        console.error('[LoadingOrchestrator] 🚨 DEADLOCK DETECTED - App-level loading stuck');
        console.error('Stuck states:', {
          appLevel: { initializing, roleLoading, menuLoading, schoolLoading },
          pageLevel: pageLoadingStates
        });
        
        // Force app-level completion by setting error flag
        // This ensures content shows even if some background operations fail
        console.error('[LoadingOrchestrator] 🔧 FORCING: App will load with degraded functionality');
      }
    }, DEADLOCK_TIMEOUT);
    
    return () => clearTimeout(deadlockTimer);
  }, [isLoading, initializing, roleLoading, menuLoading, schoolLoading, pageLoadingStates]);

  // Debug utilities (defined after isLoading and showSkeleton to avoid TDZ error)
  const getDebugInfo = useCallback(() => {
    const activeStates = Object.keys(loadingStartTimeRef.current);
    const stuckStates = activeStates.filter(key => {
      const startTime = loadingStartTimeRef.current[key];
      return Date.now() - startTime > LOADING_TIMEOUT;
    });

    return {
      appLevel: { initializing, roleLoading, menuLoading },
      pageLevel: pageLoadingStates,
      combined: { isLoading, showSkeleton },
      health: {
        totalActive: activeStates.length,
        stuckCount: stuckStates.length,
        stuckStates,
        oldestActive: Math.max(...Object.values(loadingStartTimeRef.current).map(t => Date.now() - t), 0),
        timeout: LOADING_TIMEOUT,
        deadlockTimeout: DEADLOCK_TIMEOUT
      },
      performance: loadingHistoryRef.current,
      slowLoadings: loadingHistoryRef.current.filter(h => h.duration > 3000)
    };
  }, [initializing, roleLoading, menuLoading, schoolLoading, pageLoadingStates, isLoading, showSkeleton]);

  // Expose debug utilities to window for debugging
  useEffect(() => {
    window.loadingOrchestratorDebug = getDebugInfo;
    
    // Phase 1: Enhanced debugging utilities
    window.loadingHealthCheck = () => {
      const debug = getDebugInfo();
      console.group('🔍 Loading Health Check');
      console.log('Overall Status:', debug.combined);
      console.log('App Level States:', debug.appLevel);
      console.log('Page Level States:', debug.pageLevel);
      console.log('Health Metrics:', debug.health);
      console.log('Performance:', {
        activeLoadings: debug.performance.activeLoadings,
        slowLoadings: debug.slowLoadings.length,
        recentCompletions: debug.performance.slice(-3)
      });
      console.log('Health Score:', debug.health.stuckCount === 0 ? '✅ Healthy' : '⚠️ Issues detected');
      console.log('💡 Recovery suggestions:');
      if (debug.health.stuckCount > 0) {
        console.log('- Stuck states detected - timeouts will auto-recover');
        console.log('- Try: window.location.reload() to refresh stuck states');
        console.log('- Try: Clear browser cache and reload');
      } else {
        console.log('- All loading states healthy');
      }
      console.groupEnd();
      
      return debug.health;
    };
    
    return () => {
      delete window.loadingOrchestratorDebug;
      delete window.loadingHealthCheck;
    };
  }, [getDebugInfo]);

  useEffect(() => {
    if (isAppLoading) {
      // Start timer - only show skeleton if still loading after 200ms
      const timer = setTimeout(() => {
        setShowSkeleton(true);
      }, 200);

      return () => clearTimeout(timer);
    } else {
      // Loading complete - hide skeleton immediately
      setShowSkeleton(false);
    }
  }, [isAppLoading]);

  // Enhanced debug logging with performance tracking
  const debugInfo = getDebugInfo();
  console.log('[LoadingOrchestrator] Loading states:', {
    appLevel: { initializing, roleLoading, menuLoading, schoolLoading },
    pageLevel: pageLoadingStates,
    combined: { isLoading, showSkeleton },
    health: {
      status: debugInfo.health.stuckCount === 0 ? '✅ Healthy' : '⚠️ Issues detected',
      activeLoadings: debugInfo.health.totalActive,
      stuckStates: debugInfo.health.stuckStates.length,
      oldestActiveMs: debugInfo.health.oldestActive
    },
    result: (isLoading && showSkeleton) ? 'SHOWING SKELETON' : 'SHOWING CONTENT',
    performance: {
      activeLoadings: Object.keys(loadingStartTimeRef.current),
      recentCompletions: debugInfo.performance.slice(-5),
      slowLoadingsCount: debugInfo.slowLoadings.length
    }
  });

  return (
    <LoadingOrchestratorContext.Provider value={{ 
      registerLoading, 
      getDebugInfo: getDebugInfo,
      getLoadingHistory: () => loadingHistoryRef.current,
      getActiveLoadings: () => loadingStartTimeRef.current
    }}>
      <div className="grid">
        <AnimatePresence>
          {isAppLoading && showSkeleton && (
            <motion.div
              key="skeleton"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ gridArea: '1/1' }}
            >
              {skeleton}
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!isAppLoading && (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ gridArea: '1/1' }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </LoadingOrchestratorContext.Provider>
  );
};

LoadingOrchestrator.propTypes = {
  children: PropTypes.node.isRequired,
  skeleton: PropTypes.node.isRequired
};

export default LoadingOrchestrator;
